import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import AuthField from "@/components/shared/AuthField";
import { userHome } from "@/lib/guards";
import { friendlyError } from "@/lib/errors";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Redefinição de senha: lê ?token= do e-mail, pede a nova senha (2x) e troca o
// token pela sessão — o backend revoga todos os outros dispositivos e loga aqui.
export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { resetPassword } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(!token);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não conferem.");
      return;
    }
    setLoading(true);
    try {
      const user = await resetPassword(token, password);
      toast.success("Senha redefinida com sucesso ✦");
      nav(userHome(user), { replace: true });
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "invalid_token") setInvalid(true);
      else toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12 relative overflow-hidden"
      data-testid="reset-password-page"
    >
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-10">
          <Link to="/"><BrandLogo size="lg" /></Link>
        </div>
        <div className="glass-card rounded-3xl p-8 sm:p-10">
          {invalid ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-14 h-14 rounded-full bg-brand-rose/12 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-6 h-6 text-brand-rose" />
              </div>
              <h1 className="font-display text-2xl text-white mb-2 tracking-tight">Link expirado ou inválido</h1>
              <p className="text-[14px] text-brand-ink2 mb-7">
                Este link de redefinição não é mais válido. Peça um novo — leva um instante.
              </p>
              <Link
                to="/esqueci-senha"
                data-testid="reset-request-new"
                className="pill-pink block w-full py-3.5 text-[14px] tracking-wide text-center"
              >
                Pedir um novo link
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl text-white mb-2 tracking-tight">Crie uma nova senha</h1>
              <p className="text-[14px] text-brand-ink2 mb-6">
                Você continuará conectada neste dispositivo — os outros serão desconectados.
              </p>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
                <AuthField
                  label="Nova senha" type="password" name="password" value={password} onChange={setPassword}
                  testid="reset-password" autoComplete="new-password" required
                />
                <AuthField
                  label="Confirmar nova senha" type="password" name="confirm" value={confirm} onChange={setConfirm}
                  testid="reset-confirm" autoComplete="new-password" required
                />
                <button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={loading}
                  className="pill-pink w-full py-3.5 text-[14px] tracking-wide"
                >
                  {loading ? "Salvando..." : "Redefinir senha"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
